import React, { useMemo } from "react";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Grid from "components/common/Grid";
import { usePhcTheme } from "../PhcThemeContext";
import {
  normalizeTaxEfficiency,
} from "../taxEfficiencyData";

const SECTION_ICONS = {
  taxEfficiency: "/images/phc-dashboard/tax-efficiency-icon.svg",
  dividendIncome: "/images/phc-dashboard/dividend-income-icon.svg",
  additionalMetrics: "/images/phc-dashboard/advanced-metrics-icon.svg",
};

const SECTION_MOBILE_TITLES = {
  taxEfficiency: "TAX EFFICIENCY - LTCG PROFILE",
  dividendIncome: "DIVIDEND INCOME - 1 YEAR",
  additionalMetrics: "ADVANCED METRICS - 1 YEAR",
};

const MOBILE_ADVANCED_GRID_KEYS = [
  "upMarketCapture",
  "downMarketCapture",
  "battingAverage",
  "gainLossRatio",
];

const MetricRow = ({ label, value, tone, bold, isLast }) => {
  const { theme } = usePhcTheme();

  return (
  <Box sx={{ width: "100%" }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1.5}
      sx={{ width: "100%", py: 0.25 }}
    >
      <Text
        sx={{
          fontWeight: bold ? 600 : 400,
          fontSize: { xs: 14, md: 16 },
          color: theme.textPrimary,
          flex: 1,
          minWidth: 0,
          pr: 1,
        }}
      >
        {label}
      </Text>
      <Text
        sx={{
          fontWeight: bold ? 600 : 500,
          fontSize: { xs: 14, md: 16 },
          color: (theme.taxValueTones && theme.taxValueTones[tone]) || theme.textPrimary,
          textAlign: "right",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {value ?? "—"}
      </Text>
    </Stack>
    {!isLast && (
      <Box
        sx={{
          mt: 1.5,
          mb: 1.5,
          height: "1px",
          width: "100%",
          bgcolor: theme.sectionBorder,
        }}
      />
    )}
  </Box>
  );
};

/** Mobile section heading —  */
const MobileSectionHeading = ({ sectionKey }) => {
  const { theme } = usePhcTheme();

  return (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      width: "100%",
      mb: "12px",
    }}
  >
    <Box
      component="img"
      src={SECTION_ICONS[sectionKey]}
      alt=""
      sx={{
        width:
          sectionKey === "dividendIncome"
            ? 14.67
            : sectionKey === "additionalMetrics"
              ? 12
              : 13.33,
        height:
          sectionKey === "dividendIncome"
            ? 10.67
            : sectionKey === "additionalMetrics"
              ? 12
              : 13.33,
        flexShrink: 0,
        display: "block",
      }}
    />
    <Text
      sx={{
        fontWeight: 400,
        fontSize: 14,
        color: theme.mobileLabelColor,
        letterSpacing: "0.7px",
        textTransform: "uppercase",
        lineHeight: "16px",
        whiteSpace: "nowrap",
      }}
    >
      {SECTION_MOBILE_TITLES[sectionKey]}
    </Text>
  </Box>
  );
};

const MobileAdvancedMetricsCard = ({ section }) => {
  const { theme } = usePhcTheme();
  const items = section?.items || [];
  if (!items.length) return null;
  const gridItems = items.filter((item) =>
    MOBILE_ADVANCED_GRID_KEYS.includes(item.key),
  );
  const detailItems = items.filter(
    (item) => !MOBILE_ADVANCED_GRID_KEYS.includes(item.key),
  );

  return (
    <Box sx={{ width: "100%" }}>
      <MobileSectionHeading sectionKey="additionalMetrics" />
      <Box
        sx={{
          width: "100%",
          borderRadius: "12px",
          border: theme.mobileMetaBorder,
          bgcolor: theme.mobileCardBg,
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          p: "25px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "24px",
          }}
        >
          {gridItems.map((item) => (
            <Box
              key={item.key}
              sx={{
                pb: "13px",
                borderBottom: `1px solid ${theme.mobileMetricBorder}`,
                minWidth: 0,
              }}
            >
              <Text
                sx={{
                  fontWeight: 400,
                  fontSize: 12,
                  color: theme.mobileLabelColor,
                  letterSpacing: "0.6px",
                  lineHeight: "16px",
                  mb: "2px",
                }}
              >
                {item.label}
              </Text>
              <Text
                sx={{
                  fontWeight: 400,
                  fontSize: 24,
                  color: "#2977EC",
                  lineHeight: "32px",
                  whiteSpace: "nowrap",
                }}
              >
                {item.value ?? "—"}
              </Text>
            </Box>
          ))}
        </Box>

        <Stack spacing="24px" sx={{ width: "100%" }}>
          {detailItems.map((item) => (
            <Stack
              key={item.key}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
              sx={{ width: "100%" }}
            >
              <Text
                sx={{
                  fontWeight: 400,
                  fontSize: 16,
                  color: theme.mobileValueColor,
                  lineHeight: "24px",
                  flex: 1,
                  minWidth: 0,
                  pr: 1,
                }}
              >
                {item.label}
              </Text>
              <Text
                sx={{
                  fontWeight: 400,
                  fontSize: 16,
                  color: theme.mobileLabelColor,
                  lineHeight: "24px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {item.value ?? "—"}
              </Text>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

/** Desktop card — title above card body */
const DesktopMetricCard = ({ section }) => {
  const { theme } = usePhcTheme();
  const items = section?.items || [];
  if (!items.length) return null;

  return (
    <Stack spacing={1.5} sx={{ height: "100%", width: "100%" }}>
      <Text
        sx={{
          fontWeight: 600,
          fontSize: 20,
          color: theme.textPrimary,
          minHeight: 28,
        }}
      >
        {section?.title}
      </Text>

      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 368,
          borderRadius: theme.radiusCard,
          border: `1px solid ${theme.sectionBorder}`,
          backgroundImage: theme.sectionBg,
          backgroundColor: theme.cardBg,
          boxShadow: theme.cardShadow,
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            pointerEvents: "none",
            boxShadow: theme.insetGlow,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            px: 2.5,
            py: 2.5,
          }}
        >
          {items.map((item, index) => (
            <MetricRow
              key={item.key || `${item.label}-${index}`}
              label={item.label}
              value={item.value}
              tone={item.tone}
              bold={item.bold}
              isLast={index === items.length - 1}
            />
          ))}
        </Box>
      </Box>
    </Stack>
  );
};

/** Mobile card —  (heading is outside) */
const MobileMetricCard = ({ section, sectionKey }) => {
  const { theme } = usePhcTheme();
  const items = section?.items || [];
  if (!items.length) return null;

  return (
    <Box sx={{ width: "100%" }}>
      <MobileSectionHeading sectionKey={sectionKey} />
      <Box
        sx={{
          width: "100%",
          borderRadius: "12px",
          border: theme.mobileMetaBorder,
          bgcolor: theme.mobileCardBg,
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          p: "25px",
          display: "flex",
          flexDirection: "column",
          gap: sectionKey === "dividendIncome" ? "12px" : "24px",
        }}
      >
        {items.map((item, index) => (
          <MetricRow
            key={item.key || `${item.label}-${index}`}
            label={item.label}
            value={item.value}
            tone={item.tone}
            bold={item.bold}
            isLast={index === items.length - 1}
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * Tax Efficiency + Dividend + Additional metrics
 */
const TaxEfficiencySection = ({ data }) => {
  const { theme } = usePhcTheme();
  const sections = useMemo(() => normalizeTaxEfficiency(data), [data]);

  if (!sections) return null;

  return (
    <Box sx={{ width: "100%" }}>
      {/* Mobile hero —  */}
      <Box sx={{ display: { xs: "block", md: "none" }, mb: 3 }}>
        <Text
          sx={{
            fontWeight: 400,
            fontSize: 24,
            color: "#2977EC",
            lineHeight: "32px",
            mb: "4px",
          }}
        >
          Portfolio Metrics
        </Text>
        <Text
          sx={{
            fontWeight: 400,
            fontSize: 14,
            color: theme.mobileLabelColor,
            lineHeight: "20px",
          }}
        >
          Annualized Performance & Tax Analysis
        </Text>
      </Box>

      {/* Mobile stacked sections */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          gap: 3,
          width: "100%",
        }}
      >
        <MobileMetricCard
          section={sections.taxEfficiency}
          sectionKey="taxEfficiency"
        />
        <MobileMetricCard
          section={sections.dividendIncome}
          sectionKey="dividendIncome"
        />
        <MobileAdvancedMetricsCard section={sections.additionalMetrics} />
      </Box>

      {/* Desktop 3-column grid — unchanged */}
      <Grid
        container
        spacing={2.5}
        sx={{ display: { xs: "none", md: "flex" } }}
      >
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <DesktopMetricCard section={sections.taxEfficiency} />
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <DesktopMetricCard section={sections.dividendIncome} />
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <DesktopMetricCard section={sections.additionalMetrics} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TaxEfficiencySection;
